// START IMPORTS
import { InfoOutlined } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  CardContainer,
  HeaderRow,
  HeaderTitle,
  PhoneNumberRow,
  PhoneNumber,
  FraudBadge,
  SectionDivider,
  StatusRow,
  StatusLabel,
  StatusItem,
  StatusIconWrapper,
  InfoIcon,
  SectionHeader,
  SectionTitle,
  DetailGrid,
  DetailLabel,
  DetailValue,
  OwnerRow,
  OwnerInfo,
  OwnerAvatar,
  OwnerName,
} from './PhoneReportPreviewCard.styles';
import {
  getActivityConfig,
  getDncConfig,
  getInitials,
  getLeakedConfig,
} from './phoneReportPreviewUtils';

import type { PhoneReportData } from '../../types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PhoneReportPreviewCardProps {
  data: PhoneReportData;
}
// END INTERFACES_AND_TYPES

export const PhoneReportPreviewCard = ({
  data,
}: PhoneReportPreviewCardProps) => {
  // START DERIVED_DATA_AND_UTILS
  const leaked = getLeakedConfig(data.leakedNumber);
  const activity = getActivityConfig(data.userActivity);
  const dnc = getDncConfig(data.dncStatus);
  // END DERIVED_DATA_AND_UTILS

  // START RENDER
  return (
    <CardContainer>
      <HeaderRow>
        <PhoneIcon fontSize="small" />
        <HeaderTitle>Phone Report</HeaderTitle>
      </HeaderRow>

      <PhoneNumberRow>
        <PhoneNumber>{data.phoneNumber}</PhoneNumber>
        <FraudBadge severity={data.fraudSeverity ?? 'low'}>
          <WarningAmberIcon sx={{ fontSize: 16 }} />
          {data.fraudRisk}
        </FraudBadge>
      </PhoneNumberRow>

      <SectionDivider />

      <StatusRow>
        <StatusLabel>Status:</StatusLabel>
        <StatusItem>
          <StatusIconWrapper severity={leaked.severity}>
            {leaked.isWarning ? (
              <WarningAmberIcon sx={{ fontSize: 14 }} color="inherit" />
            ) : (
              <CheckIcon sx={{ fontSize: 14 }} color="inherit" />
            )}
          </StatusIconWrapper>
          Leaked Number: {data.leakedNumber}
          <InfoIcon>
            <InfoOutlined sx={{ fontSize: 12 }} />
          </InfoIcon>
        </StatusItem>
        <StatusItem>
          <StatusIconWrapper severity={activity.severity}>
            {activity.isWarning ? (
              <WarningAmberIcon sx={{ fontSize: 14 }} color="inherit" />
            ) : (
              <CheckIcon sx={{ fontSize: 14 }} color="inherit" />
            )}
          </StatusIconWrapper>
          User Activity: {data.userActivity}
          <InfoIcon>
            <InfoOutlined sx={{ fontSize: 12 }} />
          </InfoIcon>
        </StatusItem>
        <StatusItem>
          <StatusIconWrapper severity={dnc.severity}>
            {dnc.isWarning ? (
              <WarningAmberIcon sx={{ fontSize: 14 }} color="inherit" />
            ) : (
              <CheckIcon sx={{ fontSize: 14 }} color="inherit" />
            )}
          </StatusIconWrapper>
          {data.dncStatus}
          <InfoIcon>
            <InfoOutlined sx={{ fontSize: 12 }} />
          </InfoIcon>
        </StatusItem>
      </StatusRow>

      <SectionDivider />

      <SectionHeader>
        <PhoneIcon sx={{ fontSize: 18 }} />
        <SectionTitle>Line Details</SectionTitle>
      </SectionHeader>

      <DetailGrid>
        <DetailLabel>Carrier</DetailLabel>
        <DetailValue>{data.carrier}</DetailValue>
        <DetailLabel>Prepaid</DetailLabel>
        <DetailValue>{data.prepaid}</DetailValue>
        <DetailLabel>Line Type</DetailLabel>
        <DetailValue>{data.lineType}</DetailValue>
        <DetailLabel>Valid Number</DetailLabel>
        <DetailValue>{data.validNumber}</DetailValue>
        <DetailLabel>Location</DetailLabel>
        <DetailValue>{data.location}</DetailValue>
        <DetailLabel>Zip Code</DetailLabel>
        <DetailValue>{data.zipCode}</DetailValue>
        <DetailLabel>Time Zone</DetailLabel>
        <DetailValue>{data.timeZone}</DetailValue>
      </DetailGrid>

      <SectionDivider />

      <SectionHeader>
        <PersonOutlineIcon sx={{ fontSize: 18 }} />
        <SectionTitle>
          {data.ownersCount} Potential Owner{data.ownersCount !== 1 ? 's' : ''}{' '}
          Identified
        </SectionTitle>
      </SectionHeader>

      <OwnerRow>
        <OwnerInfo>
          <OwnerAvatar>{getInitials(data.ownerName)}</OwnerAvatar>
          <OwnerName>{data.ownerName}</OwnerName>
        </OwnerInfo>
      </OwnerRow>
    </CardContainer>
  );
  // END RENDER
};
